import { helpers } from "../../util/helpers.ts";
import i18n from "../../util/i18n.ts";
import { toWorker } from "../../util/toWorker.ts";
import type { View } from "../../../../../common/types.ts";

type Player = View<"roster">["players"][number];

export const ptStyles = {
  0: {
    backgroundColor: "#dc3545",
    color: "#fff",
  },
  0.75: {
    backgroundColor: "#ffc107",
    color: "#000",
  },
  1: {
    backgroundColor: "rgb(204, 204, 204)",
    color: "#000",
  },
  1.25: {
    backgroundColor: "#17a2b8",
    color: "#fff",
  },
  1.5: {
    backgroundColor: "#007bff",
    color: "#fff",
  },
};

const handlePtChange = async (p: Player, userTid: number, ptModifier: number) => {
  if (Number.isNaN(ptModifier)) {
    return;
  }

  // NEVER UPDATE AI TEAMS
  // This shouldn't be necessary, but just in case...
  if (p.tid !== userTid) {
    return;
  }

  await toWorker("main", "updatePlayingTime", { pid: p.pid, ptModifier });
};

const PlayingTime = ({ p, userTid }: { p: Player; userTid: number }) => {
  const ptModifiers = [
    {
      text: i18n.t("roster.ptBench", { ns: "ui", defaultValue: "Bench" }),
      ptModifier: "0",
    },
    {
      text: i18n.t("playerCard.roleLimited", {
        ns: "ui",
        defaultValue: "Limited",
      }),
      ptModifier: "0.75",
    },
    {
      text: i18n.t("playerCard.roleRotation", {
        ns: "ui",
        defaultValue: "Rotation",
      }),
      ptModifier: "1",
    },
    {
      text: i18n.t("playerCard.rolePriority", {
        ns: "ui",
        defaultValue: "Priority",
      }),
      ptModifier: "1.25",
    },
    {
      text: i18n.t("roster.ptFeatured", {
        ns: "ui",
        defaultValue: "Featured",
      }),
      ptModifier: "1.5",
    },
  ];

  const values = ptModifiers.map((x) => helpers.localeParseFloat(x.ptModifier));
  const index = values.findIndex((ptModifier) => ptModifier > p.ptModifier);
  let value;
  if (index === 0) {
    value = values[0];
  } else if (index > 0) {
    value = values[index - 1];
  } else {
    value = values.at(-1);
  }
  const currentValue = value ?? 1;
  const label = i18n.t("roster.playingTimeModifier", {
    ns: "ui",
    defaultValue: "Playing time modifier",
  });

  return (
    <div className="w-100" data-pt-modifier-choices="">
      <div className="form-label small mb-1">{label}</div>
      <div aria-label={label} className="d-flex flex-wrap gap-1" role="group">
        {ptModifiers.map(({ text, ptModifier }) => {
          const numericModifier = helpers.localeParseFloat(ptModifier);
          const active = numericModifier === currentValue;

          return (
            <button
              aria-pressed={active}
              className={`btn btn-sm ${active ? "btn-secondary" : "btn-outline-secondary"}`}
              data-pt-modifier-choice={ptModifier}
              key={ptModifier}
              onClick={() => handlePtChange(p, userTid, numericModifier)}
              style={active ? (ptStyles as any)[String(currentValue)] : undefined}
              type="button"
            >
              {text}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PlayingTime;
